// HubSpot CRM Integration — routes through Netlify proxy to avoid CORS
import { supabase } from './supabase';

// Portal ID — hardcoded for Ferrum's HubSpot instance. If this ever needs to
// move to a setting, update this constant and the dealUrlFor() callers.
const HUBSPOT_PORTAL_ID = '47514592';

// Build a deal record URL from just the deal id. HubSpot deal URLs follow
// a deterministic pattern, so when the proxy response or a saved row is
// missing dealUrl we can still produce a working "View →" link from the id.
export function dealUrlFor(dealId) {
  if (!dealId) return '';
  return `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-3/${dealId}`;
}

async function getToken() {
  const { data } = await supabase
    .from('pricing_settings')
    .select('value')
    .eq('key', 'hubspot_token')
    .single();
  return data?.value || null;
}

async function callProxy(action, payload = {}) {
  const token = await getToken();
  if (!token) throw new Error('HubSpot token not configured. Go to Admin → Integrations.');
  const res = await fetch('/.netlify/functions/hubspot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, token, payload })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HubSpot error (${res.status})`);
  return data;
}

export async function testConnection()    { return callProxy('test'); }
export async function searchDeals(query)  {
  const data = await callProxy('search', { query });
  return data.results || [];
}

// v3.5.99.7.15: Company typeahead search for the standalone ETF Calculator.
// Returns an array of {id, name, domain, industry, city, state}. Empty array
// when query is too short or no matches.
export async function searchCompanies(query) {
  if (!query || query.length < 2) return [];
  try {
    const data = await callProxy('search_companies', { query });
    return data.results || [];
  } catch (e) {
    console.error('searchCompanies failed:', e);
    return [];
  }
}

// v3.5.99.7.15: List deals for a selected company. Returns array of
// {id, dealname, amount, stage, closedate, createdate, description, url}.
// Used by the standalone ETF flow after a company is chosen.
export async function getCompanyDeals(companyId) {
  if (!companyId) return [];
  try {
    const data = await callProxy('get_company_deals', { companyId });
    return data.deals || [];
  } catch (e) {
    console.error('getCompanyDeals failed:', e);
    return [];
  }
}
// Always returns a response with a populated `dealUrl` — falls back to a
// constructed URL when the proxy doesn't supply one. Without this guard,
// older deals (or proxy responses missing the field) caused front-end code
// to save dealUrl='' to the quote row, which then rendered the View link
// as `<a href="">` — clicking it reloads the current page instead of
// opening HubSpot. Symptom: "View Deal" navigates back to the quote.
export async function getDealFull(dealId) {
  const full = await callProxy('get_deal_full', { dealId });
  if (full && !full.dealUrl) full.dealUrl = dealUrlFor(full.dealId || dealId);
  return full;
}
export async function createDeal(p)       {
  const deal = await callProxy('create', p);
  return { id: deal.id, url: deal.dealUrl || dealUrlFor(deal.id) };
}
export async function updateDeal(dealId, p) { return callProxy('update', { dealId, ...p }); }

// Push deal description field to HubSpot
export async function updateDealDescription(dealId, description) {
  return callProxy('update_description', { dealId, description });
}

// Post an engagement note — shows in HubSpot deal activity timeline
export async function createHubspotNote(dealId, body) {
  return callProxy('create_note', { dealId, body });
}

export async function getDealProperties() {
  return callProxy('get_deal_properties');
}

// Write quote URL into a configured HubSpot deal field.
// fieldKey comes from pricing_settings.hubspot_quote_url_field (loaded by caller).
export async function writeQuoteUrlToDeal(dealId, quoteUrl, fieldKey) {
  if (!dealId || !quoteUrl || !fieldKey) return null;
  return callProxy('update_deal_property', {
    dealId,
    propertyName:  fieldKey,
    propertyValue: quoteUrl,
  });
}

// ─── v3.5.99.7.4: bidirectional sync (Ferrum → HubSpot) ──────────────────────
//
// Two write-back modes:
//   - Gap-fill (auto, on quote save): push only fields HubSpot was missing
//     when connectDeal originally pulled. Safe — won't overwrite HubSpot data
//     that's already there. Caller passes the original snapshot.
//   - Full overwrite (manual button): push all current Ferrum values regardless
//     of HubSpot's current state. Used when rep explicitly wants to force-sync.
//
// The Netlify proxy provides raw update_contact / update_company actions.
// updateContact() and updateCompany() are thin wrappers. The orchestrators
// (pushHubspotGapFill, pushHubspotOverwrite) encapsulate the snapshot-diff
// logic and address-parsing so each quote page stays clean.

export async function updateContact(contactId, properties) {
  if (!contactId) throw new Error('contactId required');
  if (!properties || Object.keys(properties).length === 0) {
    return { skipped: true, reason: 'no properties to update' };
  }
  return callProxy('update_contact', { contactId, properties });
}

export async function updateCompany(companyId, properties) {
  if (!companyId) throw new Error('companyId required');
  if (!properties || Object.keys(properties).length === 0) {
    return { skipped: true, reason: 'no properties to update' };
  }
  return callProxy('update_company', { companyId, properties });
}

// Split a freeform "Firstname Lastname" or "Firstname Middle Lastname" into
// HubSpot's split firstname/lastname fields. First token = firstname, rest =
// lastname. Returns {} if input is empty.
function splitFullName(fullName) {
  const trimmed = (fullName || '').trim();
  if (!trimmed) return {};
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstname: parts[0] };
  return { firstname: parts[0], lastname: parts.slice(1).join(' ') };
}

// Best-effort US-address parser. Splits a freeform string like
// "1234 W Main St, Anytown, IL 60123" into HubSpot's structured fields
// (address/city/state/zip). Returns whatever it can parse — at minimum
// the first comma-separated chunk becomes `address`.
//
// Limitations: only handles US-style commas + state-zip pattern. International
// addresses or unusual formats may parse partially. The structured push is
// nice-to-have — if it fails, the freeform string still lands in the address
// field, which is usually enough for HubSpot's record display.
function parseFreeformAddress(freeform) {
  if (!freeform || !freeform.trim()) return {};
  const parts = freeform.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return {};

  const result = { address: parts[0] };
  if (parts.length === 1) return result;

  // Detect trailing country (non-numeric, multi-word OK)
  let lastIdx = parts.length - 1;
  const last = parts[lastIdx];
  const hasCountry = lastIdx >= 2 && /^[A-Za-z\s]+$/.test(last) && !/\b\d/.test(last);
  if (hasCountry) {
    result.country = last;
    lastIdx -= 1;
  }

  // The (now) last part should be "STATE ZIP" — e.g. "IL 60123" or "IL 60123-4567"
  if (lastIdx >= 1) {
    const stateZipMatch = parts[lastIdx].match(/^([A-Za-z]{2})\s+([\d\-]+)$/);
    if (stateZipMatch) {
      result.state = stateZipMatch[1].toUpperCase();
      result.zip = stateZipMatch[2];
      // Anything between parts[0] (address) and parts[lastIdx] (state+zip) is the city
      if (lastIdx >= 2) {
        result.city = parts.slice(1, lastIdx).join(', ');
      }
    } else {
      // Couldn't parse state+zip cleanly — treat everything after address as city
      result.city = parts.slice(1, lastIdx + 1).join(', ');
    }
  }

  return result;
}

// Capture the original HubSpot state at connectDeal time so we can later
// detect "this field was empty when we pulled". Stored on the quote at
// inputs.hubspotSnapshot. Persisted through saves; refreshed whenever the
// rep re-runs connectDeal.
export function captureHubspotSnapshot(full) {
  return {
    contact: {
      phone:        full?.contact?.phone        || '',
      mobilephone:  full?.contact?.mobilephone  || '',
      email:        full?.contact?.email        || '',
      firstname:    full?.contact?.firstname    || '',
      lastname:     full?.contact?.lastname     || '',
    },
    company: {
      address:      full?.company?.address      || '',
      address2:     full?.company?.address2     || '',
      city:         full?.company?.city         || '',
      state:        full?.company?.state        || '',
      zip:          full?.company?.zip          || '',
      country:      full?.company?.country      || '',
      phone:        full?.company?.phone        || '',
    },
  };
}

// Build the full HubSpot contact properties from Ferrum state. Empty values
// are omitted (HubSpot treats empty string as "clear field" — we don't want
// to nuke fields).
function buildContactProps({ recipientContact, recipientEmail, recipientPhone, recipientMobilePhone }) {
  const props = {};
  if (recipientPhone)        props.phone       = recipientPhone;
  if (recipientMobilePhone)  props.mobilephone = recipientMobilePhone;
  if (recipientEmail)        props.email       = recipientEmail;
  const { firstname, lastname } = splitFullName(recipientContact);
  if (firstname) props.firstname = firstname;
  if (lastname)  props.lastname  = lastname;
  return props;
}

function buildCompanyProps({ recipientAddress }) {
  const parsed = parseFreeformAddress(recipientAddress);
  const props = {};
  if (parsed.address)  props.address  = parsed.address;
  if (parsed.address2) props.address2 = parsed.address2;
  if (parsed.city)     props.city     = parsed.city;
  if (parsed.state)    props.state    = parsed.state;
  if (parsed.zip)      props.zip      = parsed.zip;
  if (parsed.country)  props.country  = parsed.country;
  // Note: company.phone not tracked separately in Ferrum yet — recipientPhone
  // currently maps to contact.phone. If a separate company-phone field gets
  // added later, push it here.
  return props;
}

/**
 * Gap-fill push: send only fields where the original HubSpot snapshot was
 * empty but Ferrum now has a value. Safe — won't overwrite HubSpot data.
 *
 * @param {object} opts
 * @param {string} opts.contactId       — HubSpot contact ID (skip contact push if missing)
 * @param {string} opts.companyId       — HubSpot company ID (skip company push if missing)
 * @param {object} opts.snapshot        — { contact: {...}, company: {...} } captured at connectDeal
 * @param {string} opts.recipientContact      — Ferrum state: full name
 * @param {string} opts.recipientEmail        — Ferrum state
 * @param {string} opts.recipientPhone        — Ferrum state
 * @param {string} opts.recipientMobilePhone  — Ferrum state
 * @param {string} opts.recipientAddress      — Ferrum state: freeform address
 * @returns {Promise<object>} { contactFields: [...], companyFields: [...], errors: [...], updatedSnapshot?: {...} }
 */
export async function pushHubspotGapFill(opts) {
  const { contactId, companyId, snapshot } = opts;
  const out = { contactFields: [], companyFields: [], errors: [] };

  // Contact gap-fill
  if (contactId && snapshot?.contact) {
    const fullProps = buildContactProps(opts);
    const gapProps = {};
    for (const [field, value] of Object.entries(fullProps)) {
      if (!snapshot.contact[field]) gapProps[field] = value;
    }
    if (Object.keys(gapProps).length > 0) {
      try {
        await updateContact(contactId, gapProps);
        out.contactFields = Object.keys(gapProps);
      } catch (e) { out.errors.push(`contact: ${e.message}`); }
    }
  }

  // Company gap-fill
  if (companyId && snapshot?.company) {
    const fullProps = buildCompanyProps(opts);
    const gapProps = {};
    for (const [field, value] of Object.entries(fullProps)) {
      if (!snapshot.company[field]) gapProps[field] = value;
    }
    if (Object.keys(gapProps).length > 0) {
      try {
        await updateCompany(companyId, gapProps);
        out.companyFields = Object.keys(gapProps);
      } catch (e) { out.errors.push(`company: ${e.message}`); }
    }
  }

  // Build updated snapshot reflecting what was pushed — caller persists this
  // so the next save doesn't re-push the same fields.
  if (out.contactFields.length > 0 || out.companyFields.length > 0) {
    out.updatedSnapshot = JSON.parse(JSON.stringify(snapshot || { contact: {}, company: {} }));
    if (!out.updatedSnapshot.contact) out.updatedSnapshot.contact = {};
    if (!out.updatedSnapshot.company) out.updatedSnapshot.company = {};
    const contactProps = buildContactProps(opts);
    const companyProps = buildCompanyProps(opts);
    for (const f of out.contactFields) out.updatedSnapshot.contact[f] = contactProps[f];
    for (const f of out.companyFields) out.updatedSnapshot.company[f] = companyProps[f];
  }

  return out;
}

/**
 * Full overwrite push: send ALL current Ferrum values, ignoring the snapshot.
 * Used by the manual "Sync to HubSpot" button when the rep wants to force
 * HubSpot to match Ferrum's current state.
 *
 * Same opts shape as pushHubspotGapFill (snapshot is optional, ignored here).
 */
export async function pushHubspotOverwrite(opts) {
  const { contactId, companyId } = opts;
  const out = { contactFields: [], companyFields: [], errors: [] };

  if (contactId) {
    const props = buildContactProps(opts);
    if (Object.keys(props).length > 0) {
      try {
        await updateContact(contactId, props);
        out.contactFields = Object.keys(props);
      } catch (e) { out.errors.push(`contact: ${e.message}`); }
    }
  }

  if (companyId) {
    const props = buildCompanyProps(opts);
    if (Object.keys(props).length > 0) {
      try {
        await updateCompany(companyId, props);
        out.companyFields = Object.keys(props);
      } catch (e) { out.errors.push(`company: ${e.message}`); }
    }
  }

  // After overwrite, snapshot should reflect new HubSpot state (everything we just pushed)
  if (out.contactFields.length > 0 || out.companyFields.length > 0) {
    out.updatedSnapshot = { contact: {}, company: {} };
    const contactProps = buildContactProps(opts);
    const companyProps = buildCompanyProps(opts);
    for (const f of out.contactFields) out.updatedSnapshot.contact[f] = contactProps[f];
    for (const f of out.companyFields) out.updatedSnapshot.company[f] = companyProps[f];
  }

  return out;
}
